// src/ego/pages/EgoResult.jsx

import React, { useEffect, useMemo, useRef } from "react";
import { saveEgoResult } from "../hooks/useSaveResult";
import TaEgoPersonalReport from "../report/TaEgoPersonalReport";
import { formatDuration } from "../utils/formatDuration";

/** ========== DEĞİŞMEYECEK ANA REFERANS TABLO ========== **/
const EGO_REFERENCE = [
  { percent: 5, parent: 4, adult: 5, child: 3 },
  { percent: 10, parent: 5, adult: 6, child: null },
  { percent: 15, parent: null, adult: null, child: 4 },
  { percent: 20, parent: 6, adult: 7, child: 5 },
  { percent: 22, parent: 7, adult: 8, child: null },
  { percent: 28, parent: null, adult: null, child: 6 },
  { percent: 32, parent: 8, adult: 10, child: null },
  { percent: 42, parent: 9, adult: 11, child: 7 },
  { percent: 52, parent: null, adult: null, child: null },
  { percent: 54, parent: 10, adult: null, child: null },
  { percent: 60, parent: 11, adult: 12, child: 8 },
  { percent: 70, parent: 12, adult: null, child: 9 },
  { percent: 78, parent: null, adult: 13, child: null },
  { percent: 83, parent: 13, adult: 14, child: 10 },
  { percent: 90, parent: 14, adult: null, child: null },
  { percent: 96, parent: 15, adult: 15, child: 11 },
  { percent: 100, parent: 16, adult: 16, child: 12 },
];

/** ========== 1) Kullanıcı Cevaplarından A+B, C, D Toplama ========== **/
function calculateEgoScores(userAnswers) {
  let parentCount = 0; // A + B
  let adultCount = 0; // C
  let childCount = 0; // D

  userAnswers.forEach((ans) => {
    // TA puanlama kuralı: Sadece Kabul / Accept edilen maddeler sayılır.
    // Red / Reject edilen maddeler değerlendirmeye alınmaz.
    if (!ans.accepted) return;

    const l = ans.letter;

    if (l === "A" || l === "B") parentCount++;
    else if (l === "C") adultCount++;
    else if (l === "D") childCount++;
  });

  return { parentCount, adultCount, childCount };
}

/** ========== 2) Sayılardan Yüzde Hesaplama (Referans Tabloya Göre) ========== **/
function convertToPercent(parentCount, adultCount, childCount) {
  function mapValue(type, value) {
    // 0 veya negatifse -> %0
    if (!value || value <= 0) return 0;

    let closest = EGO_REFERENCE[0];
    let minDiff = Infinity;

    EGO_REFERENCE.forEach((row) => {
      if (row[type] !== null && row[type] !== undefined) {
        const diff = Math.abs(row[type] - value);
        if (diff < minDiff) {
          minDiff = diff;
          closest = row;
        }
      }
    });

    return closest.percent;
  }

  return {
    parentPercent: mapValue("parent", parentCount),
    adultPercent: mapValue("adult", adultCount),
    childPercent: mapValue("child", childCount),
  };
}

/** ========== 3) Objeden A/B/C/D harfini akıllıca çekme ========== **/
function extractLetter(ans) {
  // Direkt string ise
  if (typeof ans === "string") {
    const val = ans.trim().toUpperCase();
    if (["A", "B", "C", "D"].includes(val)) return val;
  }

  if (!ans || typeof ans !== "object") return null;

  // Önce sık kullanılan alanlar. Bu uygulamada soru kategorisi genellikle "category" alanında gelir.
  const directKeys = ["letter", "category", "option", "selected", "choice", "answer", "value"];
  for (const key of directKeys) {
    if (ans[key] != null) {
      const val = String(ans[key]).trim().toUpperCase();
      if (["A", "B", "C", "D"].includes(val)) return val;
    }
  }

  // Hâlâ bulamadıysak: objenin tüm alanlarına bak
  for (const key of Object.keys(ans)) {
    const val = String(ans[key]).trim().toUpperCase();
    if (["A", "B", "C", "D"].includes(val)) return val;
  }

  return null;
}


/** ========== 4) Cevabın Kabul / Accept olup olmadığını anlama ========== **/
function isAcceptedAnswer(ans) {
  if (!ans || typeof ans !== "object") return false;

  if (ans.value === 1 || ans.value === true) return true;
  if (ans.accepted === true) return true;

  const acceptedTextValues = ["1", "TRUE", "KABUL", "ACCEPT", "AGREE", "YES"];
  const keys = ["value", "accepted", "selected", "choice", "answer", "status"];

  return keys.some((key) => {
    if (ans[key] == null) return false;
    const val = String(ans[key]).trim().toUpperCase();
    return acceptedTextValues.includes(val);
  });
}

/** ========== 5) Basit dikey bar bileşeni ========== **/
function EgoBar({ labelTR, labelEN, color, percent, isTR }) {
  const safePercent = Math.max(0, Math.min(100, percent || 0));

  return (
    <div
      style={{
        flex: "1 1 180px",
        minWidth: 150,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 14,
      }}
    >
      <div
        style={{
          minHeight: 44,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          fontSize: 18,
          fontWeight: 800,
          lineHeight: 1.15,
        }}
      >
        {isTR ? labelTR : labelEN}
      </div>

      <div
        style={{
          width: 54,
          height: 230,
          borderRadius: 28,
          background: "linear-gradient(180deg, #3a3a3a 0%, #2f2f2f 100%)",
          overflow: "hidden",
          display: "flex",
          alignItems: "flex-end",
          boxShadow: "inset 0 1px 6px rgba(255,255,255,0.08)",
        }}
      >
        <div
          style={{
            width: "100%",
            height: `${safePercent}%`,
            background: color,
            borderRadius: safePercent >= 96 ? 28 : "28px 28px 10px 10px",
            transition: "height 0.4s ease",
          }}
        />
      </div>

      <div
        style={{
          fontSize: 30,
          fontWeight: 900,
          lineHeight: 1,
        }}
      >
        {isTR ? `%${safePercent.toFixed(0)}` : `${safePercent.toFixed(0)}%`}
      </div>
    </div>
  );
}

/** ========== 6) SONUÇ EKRANI KOMPONENTİ ========== **/
export default function EgoResult({ language, answers, durationMs, userInfo, accessContext }) {
  const hasSavedRef = useRef(false);
  const resultDateRef = useRef(new Date());
  const isTR = language === "tr";

  const displayDuration = useMemo(
    () => formatDuration(durationMs, language),
    [durationMs, language]
  );

  const data = useMemo(() => {
    const safeAnswers = Array.isArray(answers) ? answers : [];

    // Gelen cevapları A/B/C/D kategorisine normalize et.
    // Sadece Kabul / Accept edilenler skor hesabına katılır.
    const normalized = safeAnswers
      .map((ans) => {
        const letter = extractLetter(ans);
        if (!letter) return null;
        return {
          letter,
          accepted: isAcceptedAnswer(ans),
        };
      })
      .filter(Boolean);

    const counts = calculateEgoScores(normalized);
    const percents = convertToPercent(
      counts.parentCount,
      counts.adultCount,
      counts.childCount
    );

    return {
      totalAnswers: safeAnswers.length,
      counts,
      percents,
    };
  }, [answers]);

  const participantName = useMemo(() => {
    const rawName =
      userInfo?.name ||
      userInfo?.fullName ||
      userInfo?.adSoyad ||
      userInfo?.participantName ||
      "";

    const cleanName = String(rawName).trim();

    if (cleanName) return cleanName;
    return isTR ? "Anonim Katılımcı" : "Anonymous Participant";
  }, [userInfo, isTR]);

  const displayDate = useMemo(() => {
    const locale = isTR ? "tr-TR" : "en-US";

    return new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(resultDateRef.current);
  }, [isTR]);

  useEffect(() => {
    if (hasSavedRef.current) return;
    if (!Array.isArray(answers) || answers.length === 0) return;

    hasSavedRef.current = true;

    const scores = {
      parentCount: data.counts.parentCount,
      adultCount: data.counts.adultCount,
      childCount: data.counts.childCount,
      parentPercent: data.percents.parentPercent,
      adultPercent: data.percents.adultPercent,
      childPercent: data.percents.childPercent,
      totalAnswers: data.totalAnswers,
      calculationRule: "accept-only",
      scoringVersion: "TA-EGO-v1",
    };

    saveEgoResult(userInfo || {}, answers, scores, accessContext, language, durationMs);
  }, [answers, data, userInfo, accessContext, language, durationMs]);

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "42px 20px 56px",
        fontFamily: "Arial, sans-serif",
        color: "white",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          maxWidth: 980,
          margin: "0 auto",
        }}
      >
        <h1
          style={{
            textAlign: "center",
            margin: "0 0 12px",
            fontSize: "clamp(30px, 4vw, 46px)",
            fontWeight: 900,
            letterSpacing: "-0.03em",
          }}
        >
          {isTR ? "Ego Durum Sonuçların" : "Your Ego State Results"}
        </h1>

        <p
          style={{
            textAlign: "center",
            margin: "0 0 28px",
            fontSize: 17,
            fontWeight: 600,
            opacity: 0.92,
          }}
        >
          {isTR
            ? "Sonuçların aşağıda gösterilmiştir."
            : "Your results are shown below."}
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 0,
            border: "1px solid rgba(255,255,255,0.22)",
            borderRadius: 18,
            overflow: "hidden",
            marginBottom: 34,
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.035), rgba(255,255,255,0.015))",
            boxShadow: "0 18px 44px rgba(0,0,0,0.28)",
          }}
        >
          <div
            style={{
              padding: "26px 30px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 18,
              minHeight: 84,
            }}
          >
            <span style={{ fontSize: 34, lineHeight: 1 }}>♙</span>
            <div style={{ fontSize: 19, fontWeight: 700 }}>
              {isTR ? "İsim: " : "Name: "}
              <span style={{ fontWeight: 500 }}>{participantName}</span>
            </div>
          </div>

          <div
            style={{
              padding: "26px 30px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 18,
              minHeight: 84,
              borderLeft: "1px solid rgba(255,255,255,0.16)",
            }}
          >
            <span style={{ fontSize: 34, lineHeight: 1 }}>▣</span>
            <div style={{ fontSize: 19, fontWeight: 700 }}>
              {isTR ? "Tarih: " : "Date: "}
              <span style={{ fontWeight: 500 }}>{displayDate}</span>
            </div>
          </div>

          <div
            style={{
              padding: "26px 30px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 18,
              minHeight: 84,
              borderLeft: "1px solid rgba(255,255,255,0.16)",
            }}
          >
            <span style={{ fontSize: 34, lineHeight: 1 }}>⏱</span>
            <div style={{ fontSize: 19, fontWeight: 700 }}>
              {isTR ? "Süre: " : "Duration: "}
              <span style={{ fontWeight: 500 }}>{displayDuration}</span>
            </div>
          </div>
        </div>

        <div
          style={{
            background: "linear-gradient(180deg, #121212 0%, #101010 100%)",
            borderRadius: 32,
            padding: "38px 36px 46px",
            boxShadow: "0 20px 54px rgba(0,0,0,0.42)",
          }}
        >
          <h2
            style={{
              marginTop: 0,
              marginBottom: 34,
              textAlign: "center",
              fontSize: 32,
              fontWeight: 900,
              letterSpacing: "-0.02em",
            }}
          >
            {isTR ? "EGO Sonuçları" : "EGO Results"}
          </h2>

          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "space-around",
              gap: 28,
              flexWrap: "wrap",
            }}
          >
            <EgoBar
              labelTR="Ebeveyn Ego"
              labelEN="Parent Ego"
              color="linear-gradient(180deg, #ff7a35 0%, #ff6426 100%)"
              percent={data.percents.parentPercent}
              isTR={isTR}
            />
            <EgoBar
              labelTR="Yetişkin Ego"
              labelEN="Adult Ego"
              color="linear-gradient(180deg, #49c7ff 0%, #1eaeea 100%)"
              percent={data.percents.adultPercent}
              isTR={isTR}
            />
            <EgoBar
              labelTR="Çocuk Ego"
              labelEN="Child Ego"
              color="linear-gradient(180deg, #69d977 0%, #45c961 100%)"
              percent={data.percents.childPercent}
              isTR={isTR}
            />
          </div>
        </div>

        <TaEgoPersonalReport
          scores={data.percents}
          userInfo={userInfo}
          timestamp={displayDate}
          durationMs={durationMs}
          language={language}
          accessContext={accessContext}
        />
      </div>
    </div>
  );
}
